import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cleanTechniqueName, cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';
import { getTechniqueName as getTechniqueNameHelper } from '@/shared/hooks/useTechniques';

interface PartnerFeedbackModalProps {
  isOpen: boolean;
  onClose: (wasSaved?: boolean) => void;
  partner: {
    id: string;
    partner_name: string;
    partner_username?: string;
    partner_avatar_url?: string;
    partner_belt_rank?: string;
    partner_user_id?: string;
    is_manual?: boolean;
  };
  trainingId: string | null;
  trainingNotesId?: string | null;
  existingNotes?: PartnerNotesData | null;
  mode?: 'create' | 'view';
  rollCategories?: import('@/features/calendar/components/RollCategoriesSection').IRollCategories;
  onRollCategoriesChange?: (categories: import('@/features/calendar/components/RollCategoriesSection').IRollCategories) => void;
  getRollCategories?: () => import('@/features/calendar/components/RollCategoriesSection').IRollCategories;
}

interface PartnerNotesData {
  id?: string;
  personal_techniques_applied: string[];
  personal_notes?: string;
  partner_techniques_applied: string[];
  feedback_notes?: string;
  partner_applied_techniques?: string[];
  created_at?: string;
  updated_at?: string;
}

interface FeedbackData {
  id?: string;
  technique_ids: string[];
  feedback_text?: string;
  created_at?: string;
}

// Componente reutilizável para técnicas com collapsible
const TechniqueCollapsible = ({ 
  selectedTechniques = [], 
  onTechniqueToggle, 
  readOnly = false,
  getTechniqueName,
  techniques = [],
  t
}: {
  selectedTechniques?: string[];
  onTechniqueToggle?: (technique: string) => void;
  readOnly?: boolean;
  getTechniqueName?: (techniqueId: string) => string;
  techniques?: any[];
  t: (key: string) => string;
}) => {
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => 
      prev.includes(categoryName) 
        ? prev.filter(cat => cat !== categoryName)
        : [...prev, categoryName]
    );
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  if (readOnly) {
    // Modo somente leitura - mostrar apenas técnicas selecionadas
    return (
      <div className="space-y-3">
        {selectedTechniques.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedTechniques.map((techniqueId) => {
              // Find the technique by ID in the techniques array
              const technique = techniques.find(t => t.id.toString() === techniqueId);
              const techniqueName = technique ? technique.name : techniqueId;
              
              return (
                <Badge key={techniqueId} variant="secondary" className="text-xs bg-sossiety-red text-white">
                  {getTechniqueName ? getTechniqueName(techniqueId) : techniqueName}
                </Badge>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-xs text-gray-500">{t('calendar.partner_notes.no_techniques_selected')}</p>
          </div>
        )}
      </div>
    );
  }

  // Modo interativo - mostrar collapsible com todas as técnicas
  return (
    <div className="space-y-3">
      {/* Collapsible com todas as técnicas */}
      <div className="space-y-3">
        {/* Group techniques by category */}
        {(() => {
          const techniquesByCategory = techniques.reduce((acc, technique) => {
            const category = technique.category || 'Uncategorized';
            if (!acc[category]) {
              acc[category] = [];
            }
            acc[category].push(technique);
            return acc;
          }, {} as Record<string, any[]>);

          // Sort categories according to the defined order
          const sortedCategories = Object.entries(techniquesByCategory).sort(([aName], [bName]) => {
            const CATEGORY_ORDER = [
              'Positions',
              'Guard Passes',
              'Guards',
              'Submissions',
              'Takedowns',
              'Defense & Escapes'
            ];
            const categoryOrderMap = new Map(CATEGORY_ORDER.map((name, index) => [name.toLowerCase(), index]));
            const aIndex = categoryOrderMap.get(aName.toLowerCase()) ?? Infinity;
            const bIndex = categoryOrderMap.get(bName.toLowerCase()) ?? Infinity;
            if (aIndex !== Infinity && bIndex !== Infinity) {
              return aIndex - bIndex;
            }
            if (aIndex !== Infinity) return -1;
            if (bIndex !== Infinity) return 1;
            return aName.localeCompare(bName);
          });

          return sortedCategories.map(([categoryName, categoryTechniques]) => (
            <Collapsible 
              key={categoryName} 
              open={isCategoryOpen(categoryName)} 
              onOpenChange={() => toggleCategory(categoryName)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <span>{categoryName}</span>
                {isCategoryOpen(categoryName) ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="flex flex-wrap gap-1">
                  {(categoryTechniques as any[]).map((technique) => {
                    const techniqueId = technique.id.toString();
                    const isSelected = selectedTechniques.includes(techniqueId);
                    return (
                      <button
                        key={techniqueId}
                        type="button"
                        onClick={() => onTechniqueToggle?.(techniqueId)}
                        className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                          isSelected
                            ? 'bg-sossiety-red text-white border-sossiety-red'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {getTechniqueName ? getTechniqueName(techniqueId) : technique.name}
                      </button>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ));
        })()}
      </div>
    </div>
  );
};

export const PartnerFeedbackModal = ({
  isOpen,
  onClose,
  partner,
  trainingId,
  trainingNotesId,
  existingNotes,
  mode = 'create',
  rollCategories = {},
  onRollCategoriesChange,
  getRollCategories
}: PartnerFeedbackModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { t, currentLanguage } = useI18n();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'personal' | 'feedback'>('personal');
  
  // Notes data state
  const [notesData, setNotesData] = useState<PartnerNotesData>({
    personal_techniques_applied: [],
    personal_notes: '',
    partner_techniques_applied: [],
    feedback_notes: '',
    partner_applied_techniques: []
  });
  
  // UI state
  const [techniques, setTechniques] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [techniquesLoading, setTechniquesLoading] = useState(false);
  const [showTechniques, setShowTechniques] = useState(false);
  const [showPartnerTechniques, setShowPartnerTechniques] = useState(false);
  const [showPartnerAppliedTechniques, setShowPartnerAppliedTechniques] = useState(false);
  
  // Edit mode state - allows switching from view to edit mode
  const [isEditing, setIsEditing] = useState(false);
  
  // Determine the actual mode: if mode is 'view' but isEditing is true, allow editing
  const effectiveMode = mode === 'view' && isEditing ? 'create' : mode;
  
  // Local state for roll categories to track changes within the modal
  const [localRollCategories, setLocalRollCategories] = useState<import('@/features/calendar/components/RollCategoriesSection').IRollCategories>({});
  
  // Initialize notes data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Always check hook state first (it has the most up-to-date data from user selections)
      const hookRollCategories = getRollCategories ? getRollCategories() : null;
      
      if (isOpen && existingNotes) {
        setNotesData(existingNotes);
        // Load roll_categories from existingNotes if they exist (this is the source of truth from DB)
        if (existingNotes.roll_categories) {
          const dbRollCategories = existingNotes.roll_categories;
          // Ensure it's an object, not a string (in case it's stored as JSON string)
          const parsedRollCategories = typeof dbRollCategories === 'string' 
            ? JSON.parse(dbRollCategories) 
            : dbRollCategories;
          
          // Prefer hook state if it has data (user may have selected categories before opening modal)
          // Otherwise use DB data
          if (hookRollCategories && Object.keys(hookRollCategories).length > 0) {
            setLocalRollCategories(hookRollCategories);
          } else {
            setLocalRollCategories(parsedRollCategories);
            // Update the hook state with DB data
            if (onRollCategoriesChange) {
              onRollCategoriesChange(parsedRollCategories);
            }
          }
        } else {
          // If no roll_categories in existingNotes, use hook state if available
          if (hookRollCategories && Object.keys(hookRollCategories).length > 0) {
            setLocalRollCategories(hookRollCategories);
          } else {
            setLocalRollCategories({});
          }
        }
        setIsEditing(false); // Reset edit state when modal opens
      } else if (isOpen) {
        setNotesData({
          personal_techniques_applied: [],
          personal_notes: '',
          partner_techniques_applied: [],
          feedback_notes: '',
          partner_applied_techniques: []
        });
        // Get the latest roll_categories from hook state for this new note
        if (hookRollCategories && Object.keys(hookRollCategories).length > 0) {
          setLocalRollCategories(hookRollCategories);
        } else {
          setLocalRollCategories({});
        }
        setIsEditing(false);
      }
    }
  }, [isOpen, existingNotes, onRollCategoriesChange, getRollCategories]);

  // If no trainingId is provided, show a message and close
  if (!trainingId) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl" data-testid="partner-feedback-modal">
          <SossietyDialogHeader 
            title="Cannot Add Feedback"
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={onClose} />
          </SossietyDialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              You need to save your training notes first before adding feedback for partners.
            </p>
            
            <Button
              onClick={() => onClose()}
              variant="primary"
              size="lg"
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }


  // Load techniques for selection
  useEffect(() => {
    if (isOpen) {
      loadTechniques();
    }
  }, [isOpen, partner.id]);

  const loadTechniques = async () => {
    if (!trainingId) {
      setTechniques([]);
      return;
    }

    try {
      setTechniquesLoading(true);
      
      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        console.error('No access token available');
        setTechniques([]);
        return;
      }
      
      // Use local Supabase functions URL in development
      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:54321'
        : import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${baseUrl}/functions/v1/get_techniques`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (response.ok && result.techniques) {
        setTechniques(result.techniques);
      } else {
        setTechniques([]);
      }
    } catch (error) {
      console.error('Error loading techniques:', error);
      setTechniques([]);
    } finally {
      setTechniquesLoading(false);
    }
  };

  const handleSave = async () => {
    if (!trainingId || (!partner.partner_user_id && !partner.is_manual)) {
      toast({
        title: 'Error',
        description: 'Missing required information to save notes.',
        variant: 'destructive',
      });
      return;
    }

    // Check if at least one field is filled (including roll categories)
    const hasPersonalData = notesData.personal_techniques_applied.length > 0 || notesData.personal_notes.trim();
    const hasFeedbackData = notesData.partner_techniques_applied.length > 0 || notesData.feedback_notes.trim() || (notesData.partner_applied_techniques && notesData.partner_applied_techniques.length > 0);
    const hookCategories = getRollCategories ? getRollCategories() : null;
    const currentRollCategories = hookCategories || localRollCategories || rollCategories || {};
    const hasRollCategories = currentRollCategories && Object.keys(currentRollCategories).length > 0 && 
      Object.values(currentRollCategories).some(v => v && typeof v === 'string' && v.trim() !== '');
    
    if (!hasPersonalData && !hasFeedbackData && !hasRollCategories) {
      toast({
        title: 'Error',
        description: 'Please fill at least one field before saving.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const baseUrl = import.meta.env.DEV
        ? 'http://localhost:54321'
        : import.meta.env.VITE_SUPABASE_URL;
        
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Get the most up-to-date roll categories from the hook if available
      // This ensures we save the latest state even if the prop wasn't updated
      const hookCategories = getRollCategories ? getRollCategories() : null;
      const currentRollCategories = hookCategories || localRollCategories || rollCategories || {};
      
      // Filter out empty values to keep the JSON clean
      const cleanedRollCategories = Object.fromEntries(
        Object.entries(currentRollCategories).filter(([_, value]) => value && typeof value === 'string' && value.trim() !== '')
      );
      
      const requestData = {
        training_id: trainingId,
        partner_user_id: partner.partner_user_id,
        partner_id: partner.partner_user_id ? undefined : partner.id, // Send partner_id for manual partners (when partner_user_id is null)
        personal_techniques_applied: notesData.personal_techniques_applied,
        personal_notes: notesData.personal_notes,
        partner_techniques_applied: notesData.partner_techniques_applied,
        feedback_notes: notesData.feedback_notes,
        partner_applied_techniques: notesData.partner_applied_techniques || [],
        roll_categories: cleanedRollCategories,
      };



      const response = await fetch(`${baseUrl}/functions/v1/save_partner_notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save notes: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      toast({
        title: 'Success',
        description: 'Notes saved successfully!',
      });

      // Invalidate queries to trigger refetch of training notes data
      // This ensures the TrainingCard and TrainingViewModal show "Review Notes" / "View Notes"
      await queryClient.invalidateQueries({ queryKey: ['trainings-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['classes-with-notes'] });
      await queryClient.invalidateQueries({ queryKey: ['training-notes', trainingId] });
      
      // Force refetch to ensure data is updated immediately
      await queryClient.refetchQueries({ queryKey: ['trainings-with-notes'] });
      await queryClient.refetchQueries({ queryKey: ['classes-with-notes'] });

      // If we were editing from view mode, switch back to view mode
      if (mode === 'view' && isEditing) {
        setIsEditing(false);
      }

      onClose(true);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save notes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    onClose();
  };

  const getTechniqueName = (techniqueId: string) => {
    // Convert to string to ensure consistency
    const idStr = techniqueId.toString();
    
    // First try to find by ID
    const technique = techniques.find(t => t.id.toString() === idStr);
    
    if (technique) {
      // Use the helper function to get localized name (considers name_br)
      const techniqueObj = {
        id: technique.id,
        name: technique.name,
        name_br: technique.name_br || undefined,
        category: technique.category || ''
      };
      return getTechniqueNameHelper(techniqueObj, currentLanguage);
    }
    
    // If not found by ID, try to find by name (in case techniques are saved as names)
    const techniqueByName = techniques.find(t => t.name === idStr || t.name_br === idStr);
    
    if (techniqueByName) {
      const techniqueObj = {
        id: techniqueByName.id,
        name: techniqueByName.name,
        name_br: techniqueByName.name_br || undefined,
        category: techniqueByName.category || ''
      };
      return getTechniqueNameHelper(techniqueObj, currentLanguage);
    }
    
    // If the techniqueId looks like a number, it might be a direct technique name
    // This can happen when techniques are saved as names instead of IDs
    if (!isNaN(Number(idStr))) {
      // Try to find by index (assuming the number is a position in the techniques array)
      const techniqueByIndex = techniques[Number(idStr) - 1];
      
      if (techniqueByIndex) {
        const techniqueObj = {
          id: techniqueByIndex.id,
          name: techniqueByIndex.name,
          name_br: techniqueByIndex.name_br || undefined,
          category: techniqueByIndex.category || ''
        };
        return getTechniqueNameHelper(techniqueObj, currentLanguage);
      }
    }
    
    // If not found by ID, name, or index, return the techniqueId as is
    const cleanedId = cleanTechniqueName(idStr);
    return cleanedId;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ').filter(word => word.length > 0);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}>
        <DialogContent 
          className="w-[90vw] max-w-2xl bg-white rounded-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden"
          id="partner-notes-dialog"
          data-testid="partner-feedback-modal"
          aria-describedby="partner-notes-description"
          onPointerDownOutside={(e) => {
            // Prevent closing when clicking on the save button
            if ((e.target as HTMLElement)?.closest('button[data-testid="partner-feedback-modal-save"]')) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            // Prevent closing when clicking on the save button
            if ((e.target as HTMLElement)?.closest('button[data-testid="partner-feedback-modal-save"]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogDescription id="partner-notes-description" className="sr-only">
            Partner Notes for {partner.partner_name}
          </DialogDescription>
          <SossietyDialogHeader 
            title={
              mode === 'view' && isEditing 
                ? t('calendar.partner_notes.edit_partner_notes')
                : mode === 'view' 
                  ? t('calendar.partner_notes.view_partner_notes')
                  : t('calendar.partner_notes.add_partner_notes')
            }
            titleClassName="text-lg"
          >
            <SossietyDialogClose onClick={handleClose} />
          </SossietyDialogHeader>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
            <div className="space-y-4">
            {/* Partner Info */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-black">{t('calendar.partner_notes.your_partner')}</label>
              <div className="flex items-center space-x-3">
                <FramedAvatar
                  src={partner.partner_avatar_url}
                  alt={partner.partner_name}
                  beltRank={partner.partner_belt_rank}
                  size={40}
                  initials={getInitials(partner.partner_name)}
                  username={partner.partner_username}
                  isManual={partner.is_manual}
                />
                <div>
                  <p className="text-sm font-bold text-black">{partner.partner_name}</p>
                  {partner.partner_username && (
                    <p className="text-xs text-gray-500">@{partner.partner_username}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('personal')}
                className={`flex-1 py-2 px-4 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === 'personal'
                    ? 'border-red-600 text-black font-bold'
                    : 'border-transparent text-gray-500'
                }`}
                data-testid="partner-feedback-tab-personal"
              >
                {t('calendar.partner_notes.personal_notes')}
              </button>
              <button
                onClick={() => setActiveTab('feedback')}
                className={`flex-1 py-2 px-4 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === 'feedback'
                    ? 'border-red-600 text-black font-bold'
                    : 'border-transparent text-gray-500'
                }`}
                data-testid="partner-feedback-tab-feedback"
              >
                {t('calendar.partner_notes.share_feedback')}
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'personal' ? (
              <div className="space-y-4">
                {/* Instructional Text */}
                <p className="text-sm text-gray-600">
                  {t('calendar.partner_notes.personal_journal_description', { partner_name: partner.partner_name })}
                </p>

                {/* Techniques Applied Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-bold text-black">{t('calendar.partner_notes.techniques_applied')}</label>
                    {effectiveMode !== 'view' && (
                      <button
                        type="button"
                        onClick={() => setShowTechniques(!showTechniques)}
                        className="text-xs text-red-600 underline hover:no-underline"
                      >
                        {showTechniques ? t('calendar.partner_notes.hide_techniques') : t('calendar.partner_notes.show_techniques')}
                      </button>
                    )}
                  </div>
                  
                  {effectiveMode !== 'view' && (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-600">{t('calendar.partner_notes.techniques_applied_description')}</p>
                      <p className="text-xs text-gray-600">{t('calendar.partner_notes.techniques_applied_description_2')}</p>
                    </div>
                  )}
                  
                  {effectiveMode !== 'view' && notesData.personal_techniques_applied.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {t('calendar.partner_notes.selected_techniques', { count: notesData.personal_techniques_applied.length })}
                    </div>
                  )}
                  
                  {notesData.personal_techniques_applied.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {notesData.personal_techniques_applied.map((techniqueId) => (
                        <Badge 
                          key={techniqueId} 
                          variant="secondary" 
                          className={`text-xs bg-red-600 text-white ${
                            effectiveMode !== 'view' ? 'hover:bg-red-700 cursor-pointer' : ''
                          }`}
                          onClick={effectiveMode !== 'view' ? () => {
                            setNotesData(prev => ({
                              ...prev,
                              personal_techniques_applied: prev.personal_techniques_applied.filter(id => id !== techniqueId)
                            }));
                          } : undefined}
                        >
                          {getTechniqueName ? getTechniqueName(techniqueId) : techniqueId} {effectiveMode !== 'view' ? '×' : ''}
                        </Badge>
                      ))}
                    </div>
                  ) : effectiveMode === 'view' ? (
                    <div className="text-xs text-gray-500">{t('calendar.partner_notes.no_techniques_selected')}</div>
                  ) : null}
                  
                  {effectiveMode !== 'view' && showTechniques && (
                    <TechniqueCollapsible
                      selectedTechniques={notesData.personal_techniques_applied}
                      onTechniqueToggle={(technique) => {
                        setNotesData(prev => ({
                          ...prev,
                          personal_techniques_applied: prev.personal_techniques_applied.includes(technique)
                            ? prev.personal_techniques_applied.filter(id => id !== technique)
                            : [...prev.personal_techniques_applied, technique]
                        }));
                      }}
                      getTechniqueName={getTechniqueName}
                      techniques={techniques}
                      t={t}
                    />
                  )}
                </div>

                {/* Personal Notes */}
                {(effectiveMode !== 'view' || (effectiveMode === 'view' && notesData.personal_notes && notesData.personal_notes.trim())) && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-black">{t('calendar.partner_notes.notes_label')}</label>
                    {effectiveMode === 'view' ? (
                      <div className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-md">
                        {notesData.personal_notes}
                      </div>
                    ) : (
                      <>
                        <Textarea
                          value={notesData.personal_notes}
                          onChange={(e) => setNotesData(prev => ({ ...prev, personal_notes: e.target.value }))}
                          placeholder={t('calendar.partner_notes.personal_notes_placeholder')}
                          className="min-h-[100px] text-sm placeholder:text-xs"
                          maxLength={2000}
                          data-testid="partner-personal-notes-textarea"
                        />
                        <div className="text-xs text-gray-500 text-right">
                          {notesData.personal_notes.length}/2000
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Techniques Applied by Training Partner Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-bold text-black">{t('calendar.partner_notes.techniques_partner_applied_to_me')}</label>
                    {effectiveMode !== 'view' && (
                      <button
                        type="button"
                        onClick={() => setShowPartnerAppliedTechniques(!showPartnerAppliedTechniques)}
                        className="text-xs text-red-600 underline hover:no-underline"
                      >
                        {showPartnerAppliedTechniques ? t('calendar.partner_notes.hide_techniques') : t('calendar.partner_notes.show_techniques')}
                      </button>
                    )}
                  </div>
                  
                  {effectiveMode !== 'view' && (
                    <div className="space-y-1">
                      <p className="text-xs text-gray-600">{t('calendar.partner_notes.techniques_partner_applied_to_me_description')}</p>
                      <p className="text-xs text-gray-600">{t('calendar.partner_notes.techniques_partner_applied_to_me_description_2')}</p>
                    </div>
                  )}
                  
                  {effectiveMode !== 'view' && notesData.partner_applied_techniques && notesData.partner_applied_techniques.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {t('calendar.partner_notes.selected_techniques', { count: notesData.partner_applied_techniques.length })}
                    </div>
                  )}
                  
                  {notesData.partner_applied_techniques && notesData.partner_applied_techniques.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {notesData.partner_applied_techniques.map((techniqueId) => (
                        <Badge 
                          key={techniqueId} 
                          variant="secondary" 
                          className={`text-xs bg-red-600 text-white ${
                            effectiveMode !== 'view' ? 'hover:bg-red-700 cursor-pointer' : ''
                          }`}
                          onClick={effectiveMode !== 'view' ? () => {
                            setNotesData(prev => ({
                              ...prev,
                              partner_applied_techniques: (prev.partner_applied_techniques || []).filter(id => id !== techniqueId)
                            }));
                          } : undefined}
                        >
                          {getTechniqueName ? getTechniqueName(techniqueId) : techniqueId} {effectiveMode !== 'view' ? '×' : ''}
                        </Badge>
                      ))}
                    </div>
                  ) : effectiveMode === 'view' ? (
                    <div className="text-xs text-gray-500">{t('calendar.partner_notes.no_techniques_selected')}</div>
                  ) : null}
                  
                  {effectiveMode !== 'view' && showPartnerAppliedTechniques && (
                    <TechniqueCollapsible
                      selectedTechniques={notesData.partner_applied_techniques || []}
                      onTechniqueToggle={(technique) => {
                        setNotesData(prev => ({
                          ...prev,
                          partner_applied_techniques: (prev.partner_applied_techniques || []).includes(technique)
                            ? (prev.partner_applied_techniques || []).filter(id => id !== technique)
                            : [...(prev.partner_applied_techniques || []), technique]
                        }));
                      }}
                      getTechniqueName={getTechniqueName}
                      techniques={techniques}
                      t={t}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Instructional Text */}
                <p className="text-sm text-gray-600">
                  {t('calendar.partner_notes.feedback_description')}
                </p>

                {/* Partner Techniques Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-bold text-black">{t('calendar.partner_notes.techniques_partner_applied')}</label>
                    {effectiveMode !== 'view' && (
                      <button
                        type="button"
                        onClick={() => setShowPartnerTechniques(!showPartnerTechniques)}
                        className="text-xs text-red-600 underline hover:no-underline"
                      >
                        {showPartnerTechniques ? t('calendar.partner_notes.hide_techniques') : t('calendar.partner_notes.show_techniques')}
                      </button>
                    )}
                  </div>
                  
                  {effectiveMode !== 'view' && notesData.partner_techniques_applied.length > 0 && (
                    <div className="text-xs text-gray-500">
                      {t('calendar.partner_notes.selected_techniques', { count: notesData.partner_techniques_applied.length })}
                    </div>
                  )}
                  
                  {notesData.partner_techniques_applied.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {notesData.partner_techniques_applied.map((techniqueId) => (
                        <Badge 
                          key={techniqueId} 
                          variant="secondary" 
                          className={`text-xs bg-red-600 text-white ${
                            effectiveMode !== 'view' ? 'hover:bg-red-700 cursor-pointer' : ''
                          }`}
                          onClick={effectiveMode !== 'view' ? () => {
                            setNotesData(prev => ({
                              ...prev,
                              partner_techniques_applied: prev.partner_techniques_applied.filter(id => id !== techniqueId)
                            }));
                          } : undefined}
                        >
                          {getTechniqueName ? getTechniqueName(techniqueId) : techniqueId} {mode !== 'view' ? '×' : ''}
                        </Badge>
                      ))}
                    </div>
                  ) : effectiveMode === 'view' ? (
                    <div className="text-xs text-gray-500">{t('calendar.partner_notes.no_techniques_selected')}</div>
                  ) : null}
                  
                  {effectiveMode !== 'view' && showPartnerTechniques && (
                    <TechniqueCollapsible
                      selectedTechniques={notesData.partner_techniques_applied}
                      onTechniqueToggle={(technique) => {
                        setNotesData(prev => ({
                          ...prev,
                          partner_techniques_applied: prev.partner_techniques_applied.includes(technique)
                            ? prev.partner_techniques_applied.filter(id => id !== technique)
                            : [...prev.partner_techniques_applied, technique]
                        }));
                      }}
                      getTechniqueName={getTechniqueName}
                      techniques={techniques}
                      t={t}
                    />
                  )}
                </div>

                {/* Feedback Notes */}
                {(effectiveMode !== 'view' || (effectiveMode === 'view' && notesData.feedback_notes && notesData.feedback_notes.trim())) && (
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-black">{t('calendar.partner_notes.notes_label')}</label>
                    {effectiveMode === 'view' ? (
                      <div className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-md">
                        {notesData.feedback_notes}
                      </div>
                    ) : (
                      <>
                        <Textarea
                          value={notesData.feedback_notes}
                          onChange={(e) => setNotesData(prev => ({ ...prev, feedback_notes: e.target.value }))}
                          placeholder={t('calendar.partner_notes.feedback_notes_placeholder')}
                          className="min-h-[100px] text-sm"
                          maxLength={2000}
                          data-testid="partner-feedback-notes-textarea"
                        />
                        <div className="text-xs text-gray-500 text-right">
                          {notesData.feedback_notes.length}/2000
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Fixed Action Buttons at bottom */}
          <div className="flex justify-end space-x-2 pt-4 pb-6 px-6 border-t border-gray-200 bg-white flex-shrink-0">
            {mode === 'view' && !isEditing ? (
              <>
                <Button
                  onClick={handleClose}
                  variant="outline-light"
                  size="lg"
                  className="flex-1"
                  data-testid="partner-feedback-modal-close"
                >
                  {t('calendar.partner_notes.close')}
                </Button>
                <Button
                  onClick={() => setIsEditing(true)}
                  disabled={loading}
                  variant="primary"
                  size="lg"
                  className="flex-1"
                  data-testid="partner-feedback-modal-edit"
                >
                  {t('calendar.partner_notes.edit')}
                </Button>
              </>
            ) : (
              <>
                {mode === 'view' && isEditing && (
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to original notes if user cancels
                      if (existingNotes) {
                        setNotesData(existingNotes);
                      }
                    }}
                    variant="outline-light"
                    size="lg"
                    className="flex-1"
                    data-testid="partner-feedback-modal-cancel"
                  >
                    {t('calendar.partner_notes.cancel')}
                  </Button>
                )}
                <Button
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!loading) {
                      try {
                        await handleSave();
                      } catch (error) {
                        console.error('Error in handleSave:', error);
                      }
                    }
                  }}
                  disabled={loading}
                  variant="primary"
                  size="lg"
                  className={mode === 'view' && isEditing ? 'flex-1' : 'w-full'}
                  style={{ pointerEvents: loading ? 'none' : 'auto' }}
                  data-testid="partner-feedback-modal-save"
                >
                  {loading ? t('calendar.partner_notes.saving') : (mode === 'view' && isEditing ? t('calendar.partner_notes.update_notes') : t('calendar.partner_notes.save_notes'))}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};